import * as turf from '@turf/turf'
import { GeoJSON } from './GeoJSON/index'
import { errors } from '../services/ruutsApi/errors'
import { customSnackbarWarning } from './Snackbar/Snackbar'
import { MINIMUM_PADDOCK_AREA } from './constants'

export const miniumFeatureHectareAllowed = MINIMUM_PADDOCK_AREA

const checkFeatureValid = (feature, message) => {
  if (GeoJSON.hectareArea(feature) < miniumFeatureHectareAllowed) {
    throw new errors.BusinessEntityError({
      message:
        message ??
        `Edición no guardada. El lote ${feature.properties.name} resulta en un área inferior al mínimo permitido de ${miniumFeatureHectareAllowed} ha.`,
    })
  }
}

const checkFeaturesValid = features => {
  features.forEach(feature => checkFeatureValid(feature))
}

export const checkIntersectionWithPerimeter = (feature, perimeter, validateFeature = false) => {
  const newGeometryInsidePerimeter = GeoJSON.intersect(feature, perimeter)
  if (!newGeometryInsidePerimeter) {
    throw new errors.BusinessEntityError({
      message: `La capa ${feature?.properties?.name || ''} resulta en un área inválida. Por favor, dibújela dentro del perímetro.`,
    })
  }
  const newFeatureInsidePerimeter = {
    ...feature,
    geometry: { ...newGeometryInsidePerimeter.geometry },
  }
  if (validateFeature) checkFeatureValid(newFeatureInsidePerimeter)
  return newFeatureInsidePerimeter
}

export const processIntersectionWithPerimeter = (feature, perimeter) => {
  const intersection = GeoJSON.intersect(feature, perimeter)
  if (!intersection) return null

  return {
    ...feature,
    geometry: { ...intersection.geometry },
  }
}

export const checkIntersectionWithExclusionAreas = (
  feature,
  exclusionAreas,
  validateFeature = false,
) => {
  if (!exclusionAreas?.length) return feature

  const croppedArea = exclusionAreas?.reduce(
    (feat, ea) => GeoJSON.difference(feat, ea) || GeoJSON.truncate(feat),
    feature,
  )

  if (validateFeature) checkFeatureValid(croppedArea)

  return croppedArea
}

export const cropExclusionAreasFromGeoJSON = (exclusionAreas, featuresToCrop) =>
  // eslint-disable-next-line implicit-arrow-linebreak
  featuresToCrop.map(featureToCrop => {
    const croppedArea = exclusionAreas?.reduce((area, exclusionArea) => {
      let polygonArea
      if (area.geometry.type === 'MultiPolygon') {
        const bboxSA = turf.bbox(area)
        polygonArea = turf.bboxPolygon(bboxSA)
      } else {
        polygonArea = area
      }
      const within = turf.booleanWithin(polygonArea, exclusionArea)
      if (within) {
        // Una sampling area totalmente cubierta por el área de exclusión no deja geometría
        // remanente útil: se marca como eliminada en vez de persistir una forma vacía.
        // eslint-disable-next-line no-param-reassign
        area.properties.isDeleted = true
      } else {
        // GeoJSON.difference devuelve null cuando las geometrías no se tocan; en ese caso se
        // conserva el área original (truncada) en lugar de perder la geometría.
        // eslint-disable-next-line no-param-reassign
        area = GeoJSON.difference(area, exclusionArea) || GeoJSON.truncate(area)
      }
      return area
    }, featureToCrop)
    return croppedArea
  })

export const cropExclusionAreasFromFeatures = (exclusionAreas, features) => {
  const geoJSONFeatures = features.map(geoJSONFeature => geoJSONFeature.toGeoJSON)
  return cropExclusionAreasFromGeoJSON(exclusionAreas, geoJSONFeatures)
}

export const checkIntersectionWithOtherFeatures = (
  feature,
  features,
  validateFeature = false,
  templateMessageError = 'Edición no guardada. Al expandir el lote {originPaddockName}, el lote {destinyPaddockName} resulta en un área inferior al mínimo permitido de {miniumFeatureHectareAllowed} ha.',
  enqueueSnackbar = null,
  closeSnackbar = null,
) => {
  const newFeatures = []

  features.forEach(f => {
    let equal = false
    try {
      equal = turf.booleanEqual(feature, f)
    } catch (error) {
      const warningMessage = `Error al comparar geometrías: ${feature?.properties?.name || 'sin nombre'} y ${f?.properties?.name || 'sin nombre'}. ${error.message}`
      if (enqueueSnackbar && closeSnackbar) {
        customSnackbarWarning(warningMessage, enqueueSnackbar, closeSnackbar)
      } else {
        console.warn('Error comparing geometries with booleanEqual:', {
          error: error.message,
          featureName: feature?.properties?.name,
          otherFeatureName: f?.properties?.name,
          featureType: feature?.geometry?.type,
          otherFeatureType: f?.geometry?.type,
        })
      }
      equal = false
    }
    const difference = GeoJSON.difference(f, feature)
    const intersect = GeoJSON.intersect(f, feature)
    let contains = false
    if (f.geometry.type === 'Polygon' && feature.geometry.type === 'Polygon') {
      try {
        contains = turf.booleanContains(feature, f)
      } catch (error) {
        const warningMessage = `Error al verificar contención de geometrías: ${feature?.properties?.name || 'sin nombre'} y ${f?.properties?.name || 'sin nombre'}. ${error.message}`
        if (enqueueSnackbar && closeSnackbar) {
          customSnackbarWarning(warningMessage, enqueueSnackbar, closeSnackbar)
        } else {
          console.warn('Error checking booleanContains:', {
            error: error.message,
            featureName: feature?.properties?.name,
            otherFeatureName: f?.properties?.name,
          })
        }
        contains = false
      }
    }

    if (!equal) {
      if (intersect && difference) {
        if (contains) {
          // El lote viejo queda enteramente absorbido por el nuevo: no le queda área propia,
          // así que se descarta en vez de conservar una diferencia vacía.
          return
        }
        if (validateFeature) {
          const replacements = {
            '{originPaddockName}': feature.properties.name,
            '{destinyPaddockName}': f.properties.name,
            '{miniumFeatureHectareAllowed}': miniumFeatureHectareAllowed,
          }
          const messageError = Object.entries(replacements).reduce(
            (message, [placeholder, value]) => message.replace(placeholder, value),
            templateMessageError,
          )
          checkFeatureValid(difference, messageError)
        }
        difference.metrics = f.metrics
        // f.properties is copied, not aliased: the next line sets .area on the difference's
        // own properties object, and aliasing it to f.properties would silently corrupt the
        // original feature's recorded area.
        difference.properties = { ...f.properties, area: GeoJSON.hectareArea(difference) }
        newFeatures.push(difference)
      } else {
        newFeatures.push(f)
      }
    }
  })

  if (validateFeature) checkFeaturesValid(features)

  return newFeatures
}

export const getRandomColor = () => {
  const color = `hsl(${Math.floor(360 * Math.random())}, ${Math.floor(
    50 + 50 * Math.random(),
  )}%, ${Math.floor(30 + 50 * Math.random())}%)`
  return color
}

export const getDeterministicColor = strInput => {
  const str = String(strInput)
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    // eslint-disable-next-line no-bitwise
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }

  const h = Math.abs(hash) % 360
  // eslint-disable-next-line no-bitwise
  const s = 50 + (Math.abs(hash >> 5) % 51) // 50-100%
  // eslint-disable-next-line no-bitwise
  const l = 30 + (Math.abs(hash >> 9) % 51) // 30-80%

  return `hsl(${h}, ${s}%, ${l}%)`
}
